import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api, getProcurementSummary, getProcurementFilters, getProcurementPipeline,
  getProcurementBest, getItemSuppliers, getProcurementStatus, setProcurementWeights,
} from "@/lib/api";
import { fmtNum, fmtMoneyShort, fmtMoney } from "@/lib/format";
import { JobProgress } from "@/components/JobProgress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import {
  UploadCloud, Boxes, Layers, Users, UserCog, GitCompareArrows, Trophy,
  ChevronLeft, ChevronRight, Search, Award, Lock, Truck,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_COLOR = {
  Requisition: "bg-slate-100 text-slate-700",
  "Order Quote": "bg-amber-50 text-amber-700",
  "Inventory Order": "bg-sky-50 text-sky-700",
  "Order Fulfilment": "bg-emerald-50 text-emerald-700",
  "Expense Order": "bg-violet-50 text-violet-700",
  "Service Order": "bg-rose-50 text-rose-700",
  "Emergency Order": "bg-red-50 text-red-700",
  Other: "bg-slate-100 text-slate-500",
};

const daysSince = (d) => (d ? Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 86400000)) : null);

export default function ProcurementSnapshot() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState({ progress: 0, stage: "" });
  const [tab, setTab] = useState("pipeline");
  const [selItem, setSelItem] = useState(null);

  const { data: summary } = useQuery({ queryKey: ["proc-summary"], queryFn: getProcurementSummary, enabled: isAdmin });
  const { data: filters } = useQuery({ queryKey: ["proc-filters"], queryFn: getProcurementFilters, enabled: isAdmin });
  const hasData = summary && summary.open_lines > 0;

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="kcard p-10 text-center border-2 border-dashed border-[var(--line)]" data-testid="proc-locked">
          <Lock className="w-9 h-9 mx-auto text-slate-300" />
          <div className="text-sm font-medium text-slate-600 mt-3">Administrator access required</div>
          <div className="text-[12px] text-slate-400 mt-1">Procurement Snapshot is only available to admin accounts.</div>
        </div>
      </div>
    );
  }

  const handleUpload = async (file) => {
    if (!isAdmin || !file) return;
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    setBusy(true); setProg({ progress: 0, stage: `Uploading ${mb} MB…` });
    const CHUNK = 5 * 1024 * 1024;
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const total = Math.ceil(file.size / CHUNK) || 1;
    try {
      let jobId = null;
      const loaded = new Array(total).fill(0);
      let next = 0;
      const sendChunk = async (i) => {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(total));
        fd.append("filename", file.name);
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        const res = await api.post("/procurement/upload/chunk", fd, {
          timeout: 300000,
          onUploadProgress: (e) => {
            loaded[i] = e.loaded || 0;
            const pct = Math.min(100, Math.round((loaded.reduce((a, b) => a + b, 0) / file.size) * 100));
            setProg({ progress: pct, stage: `Uploading ${mb} MB…` });
          },
        });
        if (res.data?.job_id) jobId = res.data.job_id;
      };
      const worker = async () => { while (next < total) { await sendChunk(next++); } };
      await Promise.all(Array.from({ length: Math.min(3, total) }, worker));
      if (!jobId) throw new Error("Upload did not complete");
      setProg({ progress: 100, stage: "Processing…" });
      const start = Date.now();
      while (true) {
        await new Promise((r) => setTimeout(r, 1500));
        const st = await getProcurementStatus(jobId);
        setProg({ progress: st.progress || 0, stage: st.stage || "Processing…" });
        if (st.status === "done") { toast.success("Procurement report loaded"); qc.invalidateQueries(); break; }
        if (st.status === "failed") { toast.error((st.errors && st.errors[0]) || "Upload failed"); break; }
        if (Date.now() - start > 10 * 60 * 1000) { toast.error("Processing timed out"); break; }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Procurement</div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Procurement Snapshot</h1>
          <p className="text-sm text-slate-500 mt-0.5">Open purchase orders from JDE — pipeline stages, assigned buyers and best-supplier recommendations.</p>
        </div>
        {isAdmin && (
          <div>
            <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="proc-upload-input" onChange={(e) => handleUpload(e.target.files[0])} />
            <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="proc-upload-btn"
              className="h-10 px-4 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" style={{ background: "var(--kcsa-red)" }}>
              <UploadCloud className="w-4 h-4" /> {busy ? "Processing…" : hasData ? "Replace JDE Report" : "Upload JDE Report"}
            </button>
            {summary?.uploaded_at && <div className="text-[11px] text-slate-400 mt-1 text-right">Last loaded {new Date(summary.uploaded_at).toLocaleString()}</div>}
          </div>
        )}
      </div>

      {busy && <JobProgress progress={prog.progress} stage={prog.stage} hint="Parsing JDE order lines and scoring suppliers." testid="proc-progress" />}

      {!hasData && !busy ? (
        <div className="kcard p-12 text-center border-2 border-dashed border-[var(--line)]" data-testid="proc-empty">
          <Truck className="w-10 h-10 mx-auto text-slate-300" />
          <div className="text-sm font-medium text-slate-600 mt-3">No procurement data loaded yet</div>
          <div className="text-[12px] text-slate-400 mt-1">{isAdmin ? "Upload your JDE ItemCode Query export to get started." : "Ask an administrator to upload the JDE report."}</div>
        </div>
      ) : hasData ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="proc-kpis">
            <Kpi icon={Layers} label="Open PO Value" value={fmtMoneyShort(summary.open_value)} tid="kpi-open-value" />
            <Kpi icon={Boxes} label="Open Lines" value={fmtNum(summary.open_lines)} tid="kpi-open-lines" />
            <Kpi icon={Users} label="Suppliers" value={fmtNum(summary.suppliers)} tid="kpi-suppliers" />
            <Kpi icon={UserCog} label="Buyers" value={fmtNum(summary.buyers)} tid="kpi-buyers" />
            <Kpi icon={GitCompareArrows} label="Multi-Supplier Items" value={fmtNum(summary.multi_supplier_items)} tid="kpi-multi" />
          </div>

          <StageBar stages={summary.by_stage} total={summary.open_value} />

          <div className="flex items-center gap-1 border-b border-[var(--line)]">
            {[["pipeline", "Procurement Pipeline"], ["best", "Best Suppliers"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`proc-tab-${k}`}
                className={`px-4 py-2 text-[13px] font-medium -mb-px border-b-2 ${tab === k ? "border-[var(--kcsa-red)] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
            ))}
          </div>

          {tab === "pipeline" ? <Pipeline filters={filters} /> : <BestSuppliers isAdmin={isAdmin} onPick={setSelItem} />}
        </>
      ) : null}

      <SupplierDialog itemNo={selItem} onClose={() => setSelItem(null)} />
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, tid }) => (
  <div className="kcard p-4" data-testid={tid}>
    <div className="flex items-center gap-2 text-slate-400"><Icon className="w-4 h-4" /><span className="label-eyebrow">{label}</span></div>
    <div className="text-2xl font-bold text-slate-900 mt-1 num">{value}</div>
  </div>
);

const StageBar = ({ stages, total }) => {
  const max = Math.max(...stages.map((s) => Math.abs(s.value)), 1);
  return (
    <div className="kcard p-5" data-testid="proc-stage-breakdown">
      <div className="label-eyebrow mb-3">Open Value by Stage</div>
      <div className="space-y-2.5">
        {stages.map((s) => (
          <div key={s.code} className="flex items-center gap-3">
            <div className="w-36 shrink-0 text-[13px] text-slate-700">{s.label}</div>
            <div className="flex-1 h-5 rounded bg-slate-100 overflow-hidden">
              <div className="h-full rounded" style={{ width: `${Math.max(2, (Math.abs(s.value) / max) * 100)}%`, background: "var(--kcsa-red)" }} />
            </div>
            <div className="w-24 text-right text-[12px] num text-slate-600">{fmtMoneyShort(s.value)}</div>
            <div className="w-20 text-right text-[11px] num text-slate-400">{fmtNum(s.lines)} lines</div>
          </div>
        ))}
      </div>
    </div>
  );
};

function Pipeline({ filters }) {
  const [stage, setStage] = useState("all");
  const [buyer, setBuyer] = useState("all");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const params = { page, page_size: 50, ...(stage !== "all" ? { stage } : {}), ...(buyer !== "all" ? { buyer_no: buyer } : {}), ...(q ? { search: q } : {}) };
  const { data } = useQuery({ queryKey: ["proc-pipeline", params], queryFn: () => getProcurementPipeline(params), keepPreviousData: true });
  const rows = data?.rows || []; const total = data?.total || 0; const pages = Math.ceil(total / 50);

  return (
    <div className="space-y-3" data-testid="proc-pipeline">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-48"><Select value={stage} onValueChange={(v) => { setStage(v); setPage(1); }}>
          <SelectTrigger className="h-9 text-[13px]" data-testid="pipeline-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All stages</SelectItem>{(filters?.stages || []).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent>
        </Select></div>
        <div className="w-52"><Select value={buyer} onValueChange={(v) => { setBuyer(v); setPage(1); }}>
          <SelectTrigger className="h-9 text-[13px]" data-testid="pipeline-buyer-filter"><SelectValue placeholder="Buyer" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All buyers</SelectItem>{(filters?.buyers || []).map((b) => <SelectItem key={b.no} value={String(b.no)}>{b.name}</SelectItem>)}</SelectContent>
        </Select></div>
        <form onSubmit={(e) => { e.preventDefault(); setQ(search); setPage(1); }} className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Item, order #, supplier or reference…" data-testid="pipeline-search"
              className="w-full h-9 rounded-md border border-[var(--line)] pl-8 pr-3 text-[13px] outline-none focus:border-slate-400" /></div>
        </form>
      </div>

      <div className="kcard p-0 overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="pipeline-table">
          <thead className="bg-[var(--surface)]"><tr>
            {["Item", "Description", "Stage", "Order #", "Supplier", "Qty to Receive", "Open Value", "Order Date", "Promised", "Days Open", "Buyer"].map((h, i) => <th key={i} className={`thead-cell px-3 py-2.5 ${["Qty to Receive", "Open Value", "Days Open"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[var(--line)] hover:bg-slate-50/60" data-testid="pipeline-row">
                <td className="px-3 py-2 num font-medium text-slate-800">{r.item_no ?? "–"}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={r.desc}>{r.desc || "–"}</td>
                <td className="px-3 py-2"><span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${STAGE_COLOR[r.stage] || STAGE_COLOR.Other}`}>{r.stage}</span></td>
                <td className="px-3 py-2 num text-slate-600">{r.order_no ?? "–"}</td>
                <td className="px-3 py-2 text-slate-700 max-w-[180px] truncate" title={r.supplier_name}>{r.supplier_name || "–"}</td>
                <td className="px-3 py-2 num text-right text-slate-700">{fmtNum(r.qty_outstanding)}</td>
                <td className="px-3 py-2 num text-right text-slate-700">{fmtMoney(r.open_value)}</td>
                <td className="px-3 py-2 num text-slate-500">{r.order_date || "–"}</td>
                <td className="px-3 py-2 num text-slate-500">{r.promised_date || "–"}</td>
                <td className="px-3 py-2 num text-right text-slate-600">{daysSince(r.order_date) ?? "–"}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.buyer || "–"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">No matching order lines.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pages={pages} total={total} onPage={setPage} />
    </div>
  );
}

function BestSuppliers({ isAdmin, onPick }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const params = { page, page_size: 50, multi_only: true, ...(q ? { search: q } : {}) };
  const { data } = useQuery({ queryKey: ["proc-best", params], queryFn: () => getProcurementBest(params), keepPreviousData: true });
  const rows = data?.rows || []; const total = data?.total || 0; const pages = Math.ceil(total / 50);
  const w = data?.weights || { lt: 0.5, cost: 0.5 };
  const [ltPct, setLtPct] = useState(null);
  const ltVal = ltPct ?? Math.round(w.lt * 100);

  const applyWeights = async () => {
    try { await setProcurementWeights({ lt: ltVal, cost: 100 - ltVal }); toast.success("Scoring weights updated"); qc.invalidateQueries({ queryKey: ["proc-best"] }); }
    catch { toast.error("Could not update weights"); }
  };

  return (
    <div className="space-y-3" data-testid="proc-best">
      <div className="flex items-center gap-3 flex-wrap justify-between">
        <form onSubmit={(e) => { e.preventDefault(); setQ(search); setPage(1); }} className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item code or description…" data-testid="best-search"
            className="w-full h-9 rounded-md border border-[var(--line)] pl-8 pr-3 text-[13px] outline-none focus:border-slate-400" />
        </form>
        <div className="flex items-center gap-2 text-[12px] text-slate-500" data-testid="best-weights">
          <span className="whitespace-nowrap">Score weighting</span>
          <span className="num font-medium text-slate-700">Lead time {ltVal}%</span>
          <input type="range" min="0" max="100" step="5" value={ltVal} onChange={(e) => setLtPct(Number(e.target.value))} disabled={!isAdmin} data-testid="best-weight-slider" className="w-28 accent-[var(--kcsa-red)]" />
          <span className="num font-medium text-slate-700">Cost {100 - ltVal}%</span>
          {isAdmin && <button onClick={applyWeights} data-testid="best-apply-weights" className="h-8 px-3 rounded-md border border-[var(--line)] text-[12px] font-medium hover:bg-slate-50">Apply</button>}
        </div>
      </div>

      <div className="kcard p-0 overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="best-table">
          <thead className="bg-[var(--surface)]"><tr>
            {["Item", "Description", "Recommended Supplier", "Avg Lead Time", "On-Time", "Avg Unit Cost", "Alternatives", "Score", ""].map((h, i) => <th key={i} className={`thead-cell px-3 py-2.5 ${["Avg Lead Time", "On-Time", "Avg Unit Cost", "Alternatives", "Score"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[var(--line)] hover:bg-slate-50/60 cursor-pointer" data-testid="best-row" onClick={() => onPick(r.item_no)}>
                <td className="px-3 py-2 num font-medium text-slate-800">{r.item_no}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[240px] truncate" title={r.desc}>{r.desc || "–"}</td>
                <td className="px-3 py-2 text-slate-800 font-medium"><span className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-emerald-600" />{r.supplier_name}</span></td>
                <td className="px-3 py-2 num text-right text-slate-700">{r.eff_lt != null ? `${fmtNum(r.eff_lt, 0)} d` : (r.avg_lt != null ? `${fmtNum(r.avg_lt, 0)} d` : "–")}</td>
                <td className="px-3 py-2 num text-right text-slate-700">{r.on_time_pct != null ? `${fmtNum(r.on_time_pct, 0)}%` : "–"}</td>
                <td className="px-3 py-2 num text-right text-slate-700">{r.avg_cost != null ? fmtMoney(r.avg_cost) : "–"}</td>
                <td className="px-3 py-2 num text-right text-slate-500">{r.supplier_count}</td>
                <td className="px-3 py-2 num text-right"><ScorePill score={r.score} /></td>
                <td className="px-3 py-2 text-right"><button className="text-[12px] text-[var(--kcsa-red)] font-medium" data-testid={`best-compare-${r.item_no}`}>Compare →</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">No items with multiple suppliers found.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pages={pages} total={total} onPage={setPage} />
    </div>
  );
}

const ScorePill = ({ score }) => {
  const pct = Math.round((1 - (score ?? 0)) * 100);
  const color = pct >= 80 ? "bg-emerald-50 text-emerald-700" : pct >= 50 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold num ${color}`}>{pct}</span>;
};

function SupplierDialog({ itemNo, onClose }) {
  const { data } = useQuery({ queryKey: ["proc-suppliers", itemNo], queryFn: () => getItemSuppliers(itemNo), enabled: !!itemNo });
  const suppliers = data?.suppliers || [];
  return (
    <Dialog open={!!itemNo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="supplier-scorecard-dialog">
        <DialogHeader>
          <DialogTitle>Supplier comparison — Item {itemNo}</DialogTitle>
          <DialogDescription>{data?.desc || "Ranked by the blended lead-time + cost score (best first)."}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[var(--surface)] sticky top-0"><tr>
              {["Supplier", "Avg Lead Time", "On-Time", "Avg Unit Cost", "Orders", "Score"].map((h, i) => <th key={i} className={`thead-cell px-3 py-2 ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {suppliers.map((s, i) => (
                <tr key={i} className={`border-t border-[var(--line)] ${s.recommended ? "bg-emerald-50/50" : ""}`} data-testid="scorecard-row">
                  <td className="px-3 py-2 font-medium text-slate-800">
                    {s.recommended && <Award className="w-3.5 h-3.5 text-emerald-600 inline mr-1.5" />}{s.supplier_name}
                    {s.recommended && <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Recommended</span>}
                  </td>
                  <td className="px-3 py-2 num text-right">{s.eff_lt != null ? `${fmtNum(s.eff_lt, 0)} d` : (s.avg_lt != null ? `${fmtNum(s.avg_lt, 0)} d` : "–")}</td>
                  <td className="px-3 py-2 num text-right">{s.on_time_pct != null ? `${fmtNum(s.on_time_pct, 0)}%` : "–"}</td>
                  <td className="px-3 py-2 num text-right">{s.avg_cost != null ? fmtMoney(s.avg_cost) : "–"}</td>
                  <td className="px-3 py-2 num text-right text-slate-500">{s.orders}</td>
                  <td className="px-3 py-2 num text-right"><ScorePill score={s.score} /></td>
                </tr>
              ))}
              {suppliers.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No supplier history for this item.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-slate-400">Score shown as 0–100 (higher is better). Lead time uses actual receipt dates where available (else planned); on-time % is actual receipt vs promised date.</div>
      </DialogContent>
    </Dialog>
  );
}

export const Pager = ({ page, pages, total, onPage }) => (
  <div className="flex items-center justify-between text-[12px] text-slate-500">
    <div>{fmtNum(total)} result{total === 1 ? "" : "s"}</div>
    <div className="flex items-center gap-2">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev" className="h-8 w-8 grid place-items-center rounded-md border border-[var(--line)] disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
      <span className="num">Page {page} of {Math.max(1, pages)}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next" className="h-8 w-8 grid place-items-center rounded-md border border-[var(--line)] disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
    </div>
  </div>
);
